import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "../Avatar";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  /** When set (including null), the option shows an avatar beside the label */
  image?: string | null;
}

export interface SelectProps {
  id?: string;
  name?: string;
  value?: string | number;
  onChange: (value: string | number) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  error?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (searchTerm: string) => void;
}

export const Select: React.FC<SelectProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = "Selecione uma opção",
  disabled = false,
  required = false,
  className = "",
  label,
  error,
  searchable = true,
  searchPlaceholder = "Buscar...",
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    openUpward: boolean;
  } | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const DROPDOWN_MAX_HEIGHT = 280;
  const DROPDOWN_GAP = 4;
  const VIEWPORT_PADDING = 12;

  const optionValueMatches = (
    optionValue: string | number,
    selectedValue?: string | number,
  ) => String(optionValue) === String(selectedValue ?? "");

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get selected option label
  const selectedOption = options.find((opt) =>
    optionValueMatches(opt.value, value),
  );
  const displayValue = selectedOption ? selectedOption.label : "";

  const handleSelect = useCallback((selectedValue: string | number) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
    setDropdownPosition(null);
  }, [onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideSelect = selectRef.current?.contains(target);
      const isClickInsideDropdown = dropdownRef.current?.contains(target);

      if (!isClickInsideSelect && !isClickInsideDropdown) {
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
        setDropdownPosition(null);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus search input when dropdown opens
      if (searchable && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, searchable]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
            handleSelect(filteredOptions[focusedIndex].value);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearchTerm("");
          setFocusedIndex(-1);
          break;
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, focusedIndex, filteredOptions, handleSelect]);

  // Scroll focused option into view
  useEffect(() => {
    if (focusedIndex >= 0 && optionsRef.current) {
      const focusedElement = optionsRef.current.children[
        focusedIndex
      ] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [focusedIndex]);

  const handleToggle = () => {
    if (!disabled) {
      const willOpen = !isOpen;
      setIsOpen(willOpen);

      if (willOpen && selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
        const spaceAbove = rect.top - VIEWPORT_PADDING;
        const openUpward =
          spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
        const availableHeight = openUpward
          ? Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove - DROPDOWN_GAP)
          : Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow - DROPDOWN_GAP);
        const maxHeight = Math.max(120, availableHeight);
        const top = openUpward
          ? rect.top - maxHeight - DROPDOWN_GAP
          : rect.bottom + DROPDOWN_GAP;

        setDropdownPosition({
          top: Math.max(
            VIEWPORT_PADDING,
            Math.min(top, window.innerHeight - maxHeight - VIEWPORT_PADDING),
          ),
          left: rect.left,
          width: rect.width,
          maxHeight,
          openUpward,
        });
      }

      if (!willOpen) {
        setSearchTerm("");
        setFocusedIndex(-1);
        setDropdownPosition(null);
      }
    }
  };

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const updatePosition = () => {
        if (selectRef.current) {
          const rect = selectRef.current.getBoundingClientRect();
          const spaceBelow =
            window.innerHeight - rect.bottom - VIEWPORT_PADDING;
          const spaceAbove = rect.top - VIEWPORT_PADDING;
          const openUpward =
            spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
          const availableHeight = openUpward
            ? Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove - DROPDOWN_GAP)
            : Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow - DROPDOWN_GAP);
          const maxHeight = Math.max(120, availableHeight);
          const top = openUpward
            ? rect.top - maxHeight - DROPDOWN_GAP
            : rect.bottom + DROPDOWN_GAP;

          setDropdownPosition({
            top: Math.max(
              VIEWPORT_PADDING,
              Math.min(top, window.innerHeight - maxHeight - VIEWPORT_PADDING),
            ),
            left: rect.left,
            width: rect.width,
            maxHeight,
            openUpward,
          });
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setFocusedIndex(-1);
    if (onSearchChange) {
      onSearchChange(newSearchTerm);
    }
  };

  const baseClasses =
    "w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl focus:ring-2 transition-colors duration-200 disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:cursor-not-allowed text-base bg-white dark:bg-slate-900";

  const borderClasses = error
    ? "border border-red-300 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
    : "border border-gray-300 dark:border-slate-600 focus:ring-sky-500 focus:border-sky-500";

  const combinedClasses = `${baseClasses} ${borderClasses} ${className}`.trim();

  return (
    <div className="w-full relative" ref={selectRef}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Hidden input for form submission */}
      <input
        type="hidden"
        id={id}
        name={name}
        value={value || ""}
        required={required}
        disabled={disabled}
      />

      {/* Select Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`${combinedClasses} flex items-center justify-between gap-2 text-left ${
          isOpen ? "ring-2 ring-sky-500 border-sky-500" : ""
        } ${!displayValue ? "text-gray-400 dark:text-slate-500" : "text-gray-900 dark:text-slate-100"}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label || placeholder}
      >
        <span className="min-w-0 flex-1 flex items-center gap-2 text-left">
          {selectedOption && "image" in selectedOption ? (
            <Avatar
              text={selectedOption.label}
              src={selectedOption.image}
              size="sm"
              variant="purple"
            />
          ) : null}
          <span className="min-w-0 flex-1 whitespace-normal break-words">
            {displayValue || placeholder}
          </span>
        </span>
        <span
          className={`text-gray-400 transition-transform duration-200 flex-shrink-0 leading-snug pt-0.5 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▼
        </span>
      </button>

      {/* Dropdown - Rendered via Portal; positioned to stay within viewport */}
      {isOpen &&
        dropdownPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[100] bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: `${dropdownPosition.maxHeight}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input - order depends on openUpward so it stays next to trigger */}
            {searchable && (
              <div
                className={`p-2 sm:p-3 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex-shrink-0 ${
                  dropdownPosition.openUpward
                    ? "border-t border-gray-200 dark:border-slate-700 order-last"
                    : ""
                }`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  className="w-full px-3 py-2 text-base border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {/* Options List - scrollable, takes remaining space */}
            <div
              ref={optionsRef}
              className="overflow-y-auto min-h-0 flex-1"
              style={{
                maxHeight: dropdownPosition.maxHeight - (searchable ? 52 : 0),
              }}
              role="listbox"
            >
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-3 sm:py-4 text-sm sm:text-base text-gray-500 dark:text-slate-400 text-center">
                  Nenhuma opção encontrada
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = optionValueMatches(option.value, value);
                  const isFocused = index === focusedIndex;
                  const isDisabled = option.disabled || false;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => !isDisabled && handleSelect(option.value)}
                      disabled={isDisabled}
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left text-sm sm:text-base transition-colors duration-150 ${
                        isSelected
                          ? "bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-300 font-medium"
                          : "text-gray-900 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-700"
                      } ${isFocused && !isSelected ? "bg-gray-100 dark:bg-slate-700" : ""} ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 flex-1 flex items-center gap-2 text-left">
                          {"image" in option ? (
                            <Avatar
                              text={option.label}
                              src={option.image}
                              size="sm"
                              variant="purple"
                            />
                          ) : null}
                          <span className="min-w-0 flex-1 whitespace-normal break-words">
                            {option.label}
                          </span>
                        </span>
                        {isSelected && (
                          <span className="text-sky-600 flex-shrink-0 leading-snug pt-0.5">
                            ✓
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
          <span className="text-red-500">⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
};
